import React from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { Form, FormActions, FormContent } from "../../../../../shared/form/form.styles";
import { useAppDispatch, useAppSelector } from "../../../../../../core/hooks/redux";
import UseAlert from "../../../../../../core/hooks/useAlert";
import getCookie from "../../../../../../core/utils/getCookie";
import Stepper from "../../../../../shared/stepper/Stepper";
import { OutlinedButton, PrimaryButton } from "../../../../../shared/native/button.styles";
import { ReactComponent as LineArrowIcon } from "../../../../../../core/static/icons/line-arrow.svg";
import steps from "./constants/steps";
import {
  HighEducationForm as HighEducationFormInterface,
  SplitHighEducationForm,
} from "./interfaces/highEducationForm";
import convertSplitHighEducationForm from "./utils/convertSplitHighEducationForm";
import { setViewedStep } from "../../../../../../store/modules/applications/highEducation/highEducationSlice";
import { saveHighEducationFormRequest } from "../../../../../../http/requests/applications/highEducation.http";
import Loader from "../../../../../shared/loaders/Loader";
import { fetchGetHighEducation } from "../../../../../../store/modules/applications/highEducation/highEducationThunk";
import ResponseBody from "../../../../../../http/models/ResponseBody";
import loading from "../../../../../../core/types/loading";

const HighEducationForm: React.FC = () => {
  const dispatch = useAppDispatch();
  const alertHandler = UseAlert();
  const navigate = useNavigate();
  const [submitLoadingStatus, setSubmitLoadingStatus] = React.useState<loading>(null);
  const { highEducationState } = useAppSelector((state) => state.modulesState.applicationsState);
  const { dictionariesState } = useAppSelector((state) => state.coreState);
  const { register, control, formState, handleSubmit, reset, setValue } =
    useForm<SplitHighEducationForm>({
      values: {
        ...highEducationState.application,
        ...highEducationState.entranceExams,
        ...highEducationState.discountsAndBenefits,
      },
      mode: "onBlur",
    });

  React.useEffect(() => {
    const cookie = getCookie();
    if (cookie?.lastModifiedHighEducationStep) {
      dispatch(setViewedStep(Number(cookie.lastModifiedHighEducationStep)));
    }
    if (cookie?.lastModifiedHighEducationApplicationId) {
      dispatch(fetchGetHighEducation(Number(cookie.lastModifiedHighEducationApplicationId)));
    }
  }, []);

  const setLastModifiedStepCookie = (step: number): void => {
    document.cookie = `lastModifiedHighEducationStep=${step}; path=/`;
  };

  const setLastModifiedApplicationCookie = (applicationId: number): void => {
    document.cookie = `lastModifiedHighEducationApplicationId=${applicationId}; path=/`;
  };

  const getCurrentFormComponent = (): ((props?: any) => React.ReactNode) => {
    return steps.find((step, key) => highEducationState.viewedStep === key)?.element;
  };

  const goToPreviousStep = (): void => {
    const previousStepIndex = highEducationState.viewedStep - 1;
    dispatch(setViewedStep(previousStepIndex));
    setLastModifiedStepCookie(previousStepIndex);
  };

  const submitForm = (stepId: number, formData: SplitHighEducationForm): void => {
    if (stepId !== 0 && !highEducationState.id) {
      return;
    }

    setSubmitLoadingStatus("LOADING");
    saveHighEducationFormRequest(
      highEducationState.id,
      convertSplitHighEducationForm(formData)
    ).then((saveRes) => {
      if (saveRes.data.error) {
        setSubmitLoadingStatus("ERROR");
        alertHandler({ message: saveRes.data.text, type: "error" });
        return;
      }
      setSubmitLoadingStatus("COMPLETE");

      setLastModifiedApplicationCookie(saveRes.data.payload?.id);
      alertHandler({ message: "Информация сохранена", type: "success" });

      dispatch(fetchGetHighEducation(saveRes.data.payload.id)).then((action) => {
        const getRes = action.payload as ResponseBody<HighEducationFormInterface>;
        if (getRes.error) {
          alertHandler({ message: getRes.text, type: "error" });
        }

        if (stepId === steps.length - 1) {
          navigate("/applications/high-education/check");
          return;
        }
        dispatch(setViewedStep(stepId + 1));
        setLastModifiedStepCookie(stepId + 1);
      });
    });
  };

  if (highEducationState.dataLoadingStatus === "LOADING") {
    return (
      <Form>
        <Loader type="blank" />
      </Form>
    );
  }

  return (
    <Form onSubmit={handleSubmit((data) => submitForm(highEducationState.viewedStep, data))}>
      <Stepper items={steps} currentStep={highEducationState.viewedStep} className="stepper" />
      {dictionariesState.programDictionariesLoadingStatus === "LOADING" ? (
        <FormContent>
          <Loader type="blank" />
        </FormContent>
      ) : (
        <FormContent>
          {getCurrentFormComponent() &&
            getCurrentFormComponent()({
              register,
              control,
              formState,
              reset,
              setValue,
            })}
        </FormContent>
      )}
      <FormActions lastStep={highEducationState.viewedStep === steps.length - 1}>
        {highEducationState.viewedStep !== 0 && (
          <OutlinedButton className="leftButton" type="button" onClick={goToPreviousStep}>
            <LineArrowIcon />
          </OutlinedButton>
        )}
        <PrimaryButton
          disabled={submitLoadingStatus === "LOADING"}
          className="rightButton"
          type="submit"
        >
          {highEducationState.viewedStep !== steps.length - 1 ? <LineArrowIcon /> : "Сохранить"}
        </PrimaryButton>
      </FormActions>
    </Form>
  );
};

export default HighEducationForm;
